import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Save, RefreshCw, Check, X, Shield } from 'lucide-react';
import { permisosApi, Permiso, RolConPermisos } from '../../apis/permisosApi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const Permisos: React.FC = () => {
  const queryClient = useQueryClient();
  const { nivel: miNivel } = useAuth();
  const [matriz, setMatriz] = useState<Record<number, Record<number, boolean>>>({});
  const [cambios, setCambios] = useState<Set<number>>(new Set()); // rol IDs con cambios

  const { data, isLoading } = useQuery({
    queryKey: ['permisos-matriz'],
    queryFn: permisosApi.getMatriz,
  });

  useEffect(() => {
    if (data?.matriz) setMatriz(data.matriz);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (rolId: number) => {
      return permisosApi.actualizarPermisos(rolId, matriz[rolId]);
    },
    onSuccess: (_, rolId) => {
      setCambios(prev => { const n = new Set(prev); n.delete(rolId); return n; });
      queryClient.invalidateQueries({ queryKey: ['permisos-matriz'] });
      toast.success('Permisos actualizados');
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Error al guardar'),
  });

  const togglePermiso = (rolId: number, permisoId: number) => {
    setMatriz(prev => ({
      ...prev,
      [rolId]: { ...prev[rolId], [permisoId]: !prev[rolId]?.[permisoId] },
    }));
    setCambios(prev => new Set(prev).add(rolId));
  };

  const guardarRol = (rolId: number) => saveMutation.mutate(rolId);

  const permisos: Permiso[] = data?.permisos || [];
  const roles: RolConPermisos[] = data?.roles || [];

  // Agrupar permisos por módulo
  const modulos: Record<string, Permiso[]> = {};
  permisos.forEach(p => {
    if (!modulos[p.modulo]) modulos[p.modulo] = [];
    modulos[p.modulo].push(p);
  });

  const moduloLabels: Record<string, string> = {
    tags: 'Tags', productos: 'Productos', variables: 'Tipos de Producto',
    dispositivos: 'Dispositivos', mapa: 'Mapa', usuarios: 'Usuarios',
    subcuentas: 'Subcuentas', permisos: 'Permisos', roles: 'Roles',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Cargando permisos...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .permisos-container { min-height: 100vh; background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%); color: white; padding: 2rem; }
        .content-wrapper { max-width: 1400px; margin: 0 auto; }
        .page-title { font-size: 2.5rem; font-weight: 700; background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 0.5rem; }
        .page-subtitle { color: rgba(255,255,255,0.7); font-size: 1.1rem; margin-bottom: 2rem; }
        .table-container { background: rgba(255,255,255,0.05); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; overflow-x: auto; margin-bottom: 2rem; }
        .perm-table { width: 100%; border-collapse: collapse; min-width: 700px; }
        .perm-table th { background: rgba(255,255,255,0.1); padding: 12px 16px; text-align: center; font-weight: 600; color: rgba(255,255,255,0.9); font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid rgba(255,255,255,0.1); position: sticky; top: 0; }
        .perm-table th:first-child { text-align: left; min-width: 220px; }
        .perm-table td { padding: 10px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); text-align: center; }
        .perm-table td:first-child { text-align: left; }
        .perm-table tr:hover td { background: rgba(255,255,255,0.03); }
        .modulo-row td { background: rgba(0,122,255,0.08); font-weight: 700; color: #007AFF; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; padding: 8px 16px; }
        .perm-name { color: rgba(255,255,255,0.8); font-size: 0.9rem; }
        .perm-desc { color: rgba(255,255,255,0.4); font-size: 0.75rem; }
        .check-btn { width: 32px; height: 32px; border-radius: 8px; border: 2px solid rgba(255,255,255,0.2); background: transparent; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .check-btn.active { background: rgba(52,199,89,0.2); border-color: #34C759; }
        .check-btn.inactive { background: rgba(255,59,48,0.1); border-color: rgba(255,59,48,0.3); }
        .check-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .check-btn:not(:disabled):hover { transform: scale(1.1); }
        .save-row { display: flex; justify-content: center; gap: 12px; padding: 12px; background: rgba(255,255,255,0.05); border-top: 1px solid rgba(255,255,255,0.1); }
        .btn { padding: 10px 20px; border-radius: 10px; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; border: none; cursor: pointer; font-size: 0.9rem; transition: all 0.3s; }
        .btn-save { background: linear-gradient(135deg, #34C759 0%, #28a745 100%); color: white; }
        .btn-save:hover { transform: translateY(-1px); box-shadow: 0 4px 15px rgba(52,199,89,0.3); }
        .btn-save:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .rol-header { display: flex; flex-direction: column; align-items: center; gap: 2px; }
        .rol-nivel { font-size: 0.65rem; color: rgba(255,255,255,0.4); font-weight: 400; text-transform: none; letter-spacing: 0; }
        .locked-icon { color: rgba(255,255,255,0.2); }
      `}} />

      <div className="permisos-container">
        <div className="content-wrapper">
          <h1 className="page-title">Gestión de Permisos</h1>
          <p className="page-subtitle">Activa o desactiva permisos para cada rol. Solo puedes editar roles con nivel inferior al tuyo.</p>

          <div className="table-container">
            <table className="perm-table">
              <thead>
                <tr>
                  <th>Permiso</th>
                  {roles.map(rol => (
                    <th key={rol.id}>
                      <div className="rol-header">
                        <span>{rol.nombre}</span>
                        <span className="rol-nivel">Nivel {rol.nivel}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(modulos).map(([modulo, permsModulo]) => (
                  <React.Fragment key={modulo}>
                    <tr className="modulo-row">
                      <td colSpan={roles.length + 1}>
                        <Shield size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                        {moduloLabels[modulo] || modulo}
                      </td>
                    </tr>
                    {permsModulo.map(permiso => (
                      <tr key={permiso.id}>
                        <td>
                          <div className="perm-name">{permiso.nombre}</div>
                          <div className="perm-desc">{permiso.descripcion}</div>
                        </td>
                        {roles.map(rol => {
                          const activo = matriz[rol.id]?.[permiso.id] ?? false;
                          const editable = rol.nivel < miNivel;
                          return (
                            <td key={rol.id}>
                              {editable ? (
                                <button
                                  className={`check-btn ${activo ? 'active' : 'inactive'}`}
                                  onClick={() => togglePermiso(rol.id, permiso.id)}
                                >
                                  {activo ? <Check size={16} color="#34C759" /> : <X size={14} color="#ff453a" />}
                                </button>
                              ) : (
                                <span className={`check-btn ${activo ? 'active' : 'inactive'}`} style={{ cursor: 'default', opacity: 0.6 }}>
                                  {activo ? <Check size={16} color="#34C759" /> : <Lock size={12} className="locked-icon" />}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {cambios.size > 0 && (
              <div className="save-row">
                {roles.filter(r => cambios.has(r.id)).map(rol => (
                  <button
                    key={rol.id}
                    className="btn btn-save"
                    onClick={() => guardarRol(rol.id)}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar {rol.nombre}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Permisos;
